import { locationRoute, menuRoute } from "../../App";
import type { MenuItem } from "../../hooks/useMenu";
import { CartBar } from "../../components/CartBar";
import { CartItem, useCartStore } from "../../store/CartStore";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import ProductPane from "../../components/ProductPane";
import { useNavigate, useSearch } from "@tanstack/react-router";
import UnavailableProduct from "@/components/UnavilableProduct";
import { useTrackingEvent } from "@/hooks/useTrackingEvent";
import { CategoriesGrid } from "@/components/CategoriesGrid";
import { Drawer } from "@/components/ui/custom/Drawer";
import { CategoriesList } from "@/components/CategoriesList";
import { Header } from "@/components/Header";
import { useFlags } from "@/hooks/useFlags";
import { LocationConfigurationContext } from "@/providers/LocationConfigurationProvider";
import { cn } from "@/lib/utils";
import { XIcon } from "lucide-react";
import { InfoDialog } from "@/components/InfoDialog";
import { useLocationMenus } from "@/hooks/useLocationMenus";
import { toast } from "sonner";
import { CartModal } from "@/components/CartModal";
import { useLocation } from "@/providers/LocationContextProvider";
import { useLocationInfo } from "@/hooks/useLocationInfo";

const MenuContext = createContext<{
  setUnavailableProductOpen: (value: boolean) => void;
  setCounterOnlyProductOpen: (value: boolean) => void;
  setUnavailableProductPausedUntil: (value: string | null) => void;
  handleOpenProduct: (value: string, isUpsale?: boolean) => void;
  handleEditCartItem: (value: CartItem) => void;
}>({
  setUnavailableProductOpen: () => null,
  setCounterOnlyProductOpen: () => null,
  setUnavailableProductPausedUntil: () => null,
  handleOpenProduct: () => null,
  handleEditCartItem: () => null,
});

const MenuPage = () => {
  const search = useSearch({
    strict: false,
  });

  const { orderType, setOrderType, name, setName } = useLocation();

  const {
    locationId: cartLocationId,
    setLocationId,
    cart,
    table,
    remove,
  } = useCartStore();

  const { menuId } = menuRoute.useParams();
  const { locationId } = locationRoute.useParams();
  const { menuLayout, categoryLayout, dialogs } = useContext(
    LocationConfigurationContext
  );

  const { availableMenus: menus } = useLocationMenus();
  const { data: locationInfo } = useLocationInfo(locationId);

  const selectedMenu = useMemo(() => {
    return menus.find((menu) => menu.id === menuId);
  }, [menus, menuId]);

  useEffect(() => {
    console.log({ selectedMenu, orderType, menuId, menus });
    if (!orderType && selectedMenu) {
      console.log("Mismatched order type, setting to menu type");
      setOrderType(selectedMenu?.type);
    }
  }, [selectedMenu, orderType, menus, menuId]);

  useEffect(() => {
    if (!name && locationInfo) {
      setName(locationInfo.name);
    }
  }, [locationInfo, name]);

  const [isOpen, setIsOpen] = useState(false);
  const [unavailableProductOpen, setUnavailableProductOpen] = useState(false);
  const [counterOnlyProductOpen, setCounterOnlyProductOpen] = useState(false);
  const [unavailableProductPausedUntil, setUnavailableProductPausedUntil] = useState<string | null>(null);

  const [selectedProduct, setSelectedProduct] = useState<MenuItem | null>(null);
  const [selectedCartItem, setSelectedCartItem] = useState<CartItem>();

  const cartShouldBeOpen = useMemo(() => {
    return (search && search.openCart) ?? false;
  }, [search]);
  const [cartIsOpen, setCartIsOpen] = useState<boolean>(cartShouldBeOpen);

  const tracking = useTrackingEvent();
  const { addonsAvailable } = useFlags();

  const navigate = useNavigate({ from: menuRoute.id });

  useEffect(() => {
    if (cartLocationId && locationId !== cartLocationId) {
      cart.forEach((item) => remove(item.lineId)); // Clear cart on mismatch
      setLocationId(locationId);
      return;
    }

    if (!cartLocationId) {
      setLocationId(locationId);
      return;
    }
  }, [cartLocationId, locationId]);

  // Make sure cart doesn't contain unavailable items
  useEffect(() => {
    const unavailableProducts = (selectedMenu?.categories ?? [])
      .map((category) => category.products)
      .flat()
      .filter((product) => product.unavailable)
      .map((product) => product.id);
    const unavailableItemsInCart = cart.filter((item) =>
      unavailableProducts.includes(item.productId)
    );

    unavailableItemsInCart.forEach((cartItem) => {
      remove(cartItem.lineId);
    });
  }, [selectedMenu]);

  const flattenedMenus = useMemo(() => {
    if (menus) {
      return menus
        .map((menu) =>
          Object.values(menu.categories).reduce(
            (acc, category) => [...acc, ...category.products],
            [] as MenuItem[]
          )
        )
        .flat();
    }
    return [];
  }, [menus]);

  useEffect(() => {
    // Check if add-ons are available and if cart includes any -> remove them
    if (!addonsAvailable) {
      const itemsWithAddons = cart.filter(
        (item) => item.addons && item.addons?.length > 0
      );
      if (itemsWithAddons.length === 0) return; // We're safe - no addons in cart

      itemsWithAddons.forEach((item) => {
        remove(item.lineId);
      });
    }
  }, [cart]);

  const handleOpenProduct = (productId: string, isUpsale?: boolean) => {
    const evaluatedProduct = flattenedMenus.find((p) => p.id === productId);

    if (!evaluatedProduct) {
      return;
    }
    try {
      tracking.capture("product_viewed", {
        locationId: locationId,
        product_id: evaluatedProduct.id,
        product_name: evaluatedProduct.name,
      });
    } catch (error) {
      console.error("Error capturing product viewed event:", error);
    }

    setSelectedProduct({
      ...evaluatedProduct,
      isUpsale: isUpsale ?? false,
    } as MenuItem & { isUpsale?: boolean });
    setIsOpen(true);
  };

  const handleCloseProduct = () => {
    setIsOpen(false);
    setTimeout(() => {
      setSelectedCartItem(undefined);
      setSelectedProduct(null);
    }, 300);
  };

  const handleEditCartItem = (cartItem: CartItem) => {
    const evaluatedProduct = flattenedMenus.find(
      (p) => p.id === cartItem.productId
    );
    if (!evaluatedProduct) return;
    setSelectedProduct(evaluatedProduct);
    setSelectedCartItem(cartItem);
    setIsOpen(true);
  };

  const handleSetCartIsOpen = (isOpen: boolean) => {
    setCartIsOpen(isOpen);

    // When opening the cart, dismiss toast notifications but keep in-cart removed items visible
    if (isOpen) {
      toast.dismiss(); // Dismiss all toast notifications
    }

    navigate({
      search: (prev: any) => ({ openCart: isOpen, table: prev.table }),
    });
  };

  useEffect(() => {
    if (!cartIsOpen && search.openCart) {
      navigate({
        search: (prev: any) => ({ openCart: undefined, table: prev.table }),
      });
    }
  }, [cartIsOpen]);

  useEffect(() => {
    if (search && search.openCart) {
      setCartIsOpen(true);
    }
  }, [search, setCartIsOpen]);
  // Update page title to actual location name
  useEffect(() => {
    if (locationInfo?.name) {
      document.title = `TableTaxi - ${locationInfo.name}`;
    }
  }, [locationInfo]);

  const firstTimeDialog = useMemo(() => {
    return dialogs?.find((dialog) => dialog.showOn === "firstVisit");
  }, [dialogs]);

  const shouldShowFirstTimeDialog = useMemo(() => {
    return (
      (firstTimeDialog && localStorage.getItem("notFirstVisit") !== "true") ||
      false
    );
  }, [dialogs]);

  const [showFirstTimeDialog, setShowFirstTimeDialog] = useState<boolean>(
    shouldShowFirstTimeDialog
  );

  useEffect(() => {
    if (!selectedMenu) {
      console.log(
        "Menu route rendered, but no selected menu, navigating to location route"
      );

      navigate({
        to: locationRoute.id,
        search: {
          table: table,
        },
      });
      return;
    }
  }, [selectedMenu]);

  const hasActiveProducts = useMemo(() => {
    const products = selectedMenu?.categories
      .map((category) => category.products)
      .flat();
    return products && products.length > 0;
  }, [selectedMenu]);

  return (
    <MenuContext.Provider
      value={{
        handleOpenProduct,
        handleEditCartItem,
        setUnavailableProductOpen,
        setCounterOnlyProductOpen,
        setUnavailableProductPausedUntil,
      }}
    >
      {firstTimeDialog && (
        <InfoDialog
          show={showFirstTimeDialog}
          onClose={() => setShowFirstTimeDialog(false)}
          confirmText={firstTimeDialog.confirmText ?? "OK"}
          canCancel={firstTimeDialog.canCancel ?? false}
          canClose={firstTimeDialog.canClose ?? true}
          title={firstTimeDialog.title}
          description={firstTimeDialog.description.trim()}
          onConfirm={() => {
            setShowFirstTimeDialog(false);
            localStorage.setItem("notFirstVisit", "true");
          }}
        />
      )}
      <UnavailableProduct
        isOpen={unavailableProductOpen}
        setIsOpen={setUnavailableProductOpen}
        type="unavailable"
        pausedUntil={unavailableProductPausedUntil}
      />
      <UnavailableProduct
        isOpen={counterOnlyProductOpen}
        setIsOpen={setCounterOnlyProductOpen}
        type="counter_only"
      />

      <main
        className={cn(
          "@container relative z-[1] bg-background",
          menuLayout === "LIST" ? " md:grid md:grid-cols-5" : ""
        )}
      >
        <aside
          className={
            menuLayout === "LIST"
              ? "contents md:block md:col-span-2 lg:col-span-1"
              : "contents"
          }
        >
          <Header menu={selectedMenu ?? menus[0]} showCategoryTabs={true} />
        </aside>

        <section
          className={cn(
            "md:col-span-3 lg:col-span-4 md:border-l",
            menuLayout === "SWIPEABLE"
              ? ""
              : "overflow-y-auto max-h-full relative"
          )}
        >
          {hasActiveProducts ? (
            <>
              {categoryLayout === "LIST" && (
                <CategoriesList categories={selectedMenu?.categories ?? []} />
              )}

              {categoryLayout === "GRID" && (
                <CategoriesGrid categories={selectedMenu?.categories ?? []} />
              )}
            </>
          ) : (
            <div className="py-4 min-h-[500px] flex flex-col bg-muted text-muted-foreground rounded-md m-4">
              <h2 className="font-medium tracking-tighter text-left px-4">
                Sorry, we're busy, and not able to take online orders at the
                moment.
              </h2>
              <div className="whitespace-pre-line text-left text-sm text-muted-foreground px-4 flex flex-col gap-y-2">
                <p>Please order inside or contact staff.</p>
              </div>
            </div>
          )}
        </section>
      </main>

      <CartBar setCartIsOpen={handleSetCartIsOpen} locationId={locationId} />

      <Drawer
        isOpen={isOpen && selectedProduct !== null}
        closeDrawer={handleCloseProduct}
        modalSize="large"
        classes={{
          root: "!z-[2005]", // Allows products to be edited while cart is open
          paper: "rounded-t-2xl !overflow-visible !bg-transparent",
        }}
      >
        {selectedProduct !== null && (
          <>
            <button
              type="button"
              className="max-md:hidden border fixed top-2.5 right-2.5 bg-muted text-muted-foreground rounded-full h-11 w-11 aspect-square flex items-center justify-center grow-0 shrink-0 z-20 shadow-xl"
              onClick={handleCloseProduct}
              aria-label="Close product"
            >
              <XIcon size={14} className="text-muted-foreground" />
            </button>
            <ProductPane
              product={selectedProduct as MenuItem}
              itemInCart={selectedCartItem}
              onClose={() => handleCloseProduct()}
            />
          </>
        )}
      </Drawer>

      <CartModal isOpen={cartIsOpen} closeModal={() => setCartIsOpen(false)} />
    </MenuContext.Provider>
  );
};

export { MenuContext };

export default MenuPage;
